import React, { useState } from 'react';
import { Plus, Download } from 'lucide-react';
import PlaylistImportModal from '@/components/PlaylistImportModal';
import PlaylistGeneratorPanel from '@/components/PlaylistGeneratorPanel';
import GeneratedPlaylistsList from '@/components/GeneratedPlaylistsList';

/**
 * Playlists Page
 *
 * Main page for playlist management.
 * Integrates:
 * - Playlist import from Spotify
 * - Playlist generation with multiple engines
 * - Generated playlists management
 */
const PlaylistsPage: React.FC = () => {
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleImportComplete = () => {
    // Refresh could trigger artist list reload if needed
    console.log('Playlists imported successfully');
  };

  const handleGenerationComplete = (playlistId: number) => {
    console.log('Playlist generated:', playlistId);
    setShowGenerator(false);
    setRefreshTrigger(prev => prev + 1); // Trigger list refresh
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Playlist Generator</h1>
          <p className="text-gray-400">
            Create amazing playlists using AI-powered recommendation engines
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={() => setImportModalOpen(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition font-semibold"
          >
            <Download size={20} />
            Import from Spotify
          </button>

          <button
            onClick={() => setShowGenerator(prev => !prev)}
            className="flex items-center gap-2 px-6 py-3 bg-spotify-green hover:bg-green-400 text-black rounded-lg transition font-semibold"
          >
            <Plus size={20} />
            {showGenerator ? 'Hide Generator' : 'Generate New Playlist'}
          </button>
        </div>

        {/* Generator Panel */}
        {showGenerator && (
          <div className="mb-8">
            <PlaylistGeneratorPanel onGenerationComplete={handleGenerationComplete} />
          </div>
        )}

        {/* Divider */}
        {showGenerator && (
          <div className="border-t border-gray-800 my-8"></div>
        )}

        {/* Generated Playlists List */}
        <GeneratedPlaylistsList refreshTrigger={refreshTrigger} />

        {/* Import Modal */}
        <PlaylistImportModal
          isOpen={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onImportComplete={handleImportComplete}
        />
      </div>
    </div>
  );
};

export default PlaylistsPage;
